import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routeKeys";
import account from "../../assets/svgs/account.svg";
import Eye from "../../assets/svgs/eye.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import SmartPlanFilter from "../../components/FilterComponents/SmartPlanFilter";
import { Skeleton } from "@chakra-ui/react";
import ContactPagination from "../../components/Pagination/ContactPagination";
import DataAsOfNotice from "../../components/DataAsOfNotice";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import Loader from "../../components/Loader";
import { BASE_URL } from "../../utils/Element";
import { initialSmartPlanFilterData } from "../../utils/initialData";
import SmartplanBarcharts from "./components/charts/SmartplanBarcharts";
import CreateSmartPlanModal from "./modals/CreateSmartPlanModal";
import LinkContactInfoModal from "./modals/LinkContactInfoModal";
import "./Smartplan.css";

// Enhanced Contact Tags with Expand/Collapse
const ContactTagsList = ({ names }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxVisible = 4;

  if (!names || names === "-" || names.trim() === "") {
    return <span className="dark-M body-N">None</span>;
  }

  const nameArray = names.split(",").map((name) => name.trim()).filter(Boolean);
  const visibleNames = isExpanded ? nameArray : nameArray.slice(0, maxVisible);
  const hiddenCount = nameArray.length - maxVisible;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {visibleNames.map((name, index) => (
          <span
            key={index}
            className="inline-block px-3 py-1 text-sm body-N dark-H bg-gray-100 rounded-full border border-gray-300"
          >
            {name}
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-blue-600 body-S underline cursor-pointer hover:text-blue-800 self-start"
        >
          {isExpanded ? "Show less" : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};

const Smartplan = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);

  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [smartPlanData, setSmartPlanData] = useState([]);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [selectedContacts, setSelectedContact] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });
  const [contactCounts, setContactCounts] = useState();
  const [smartplanDashboard, setSmartplanDashboard] = useState();
  const [touchPlanUserData, setTouchPlanUserData] = useState([]);
  const [touchPlanTasksData, setTouchPlanTasksData] = useState([]);
  const [touchPlanPaginationData, setTouchPlanPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });
  const [pageNumberTab2, setPageNumberTab2] = useState(1);
  const [itemPerPageTab2, setItemPerPageTab2] = useState(50);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialSmartPlanFilterData;
  });

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    const trimmed = value.startsWith(" ") ? value.trimStart() : value;
    setSearch(trimmed);
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Only update if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim());
      }
    }, 450);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  // Sync with URL query param (optional but kept your original behavior)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlSearch = queryParams.get("search") ?? "";
    setSearch(urlSearch);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (debouncedSearch) {
      queryParams.set("search", debouncedSearch);
    } else {
      queryParams.delete("search");
    }
    navigate({ search: queryParams.toString() }, { replace: true });
  }, [debouncedSearch, navigate, location.search]);

  const handleNext = () => {
    if (pageNumber < paginationData.totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePrev = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const buildQueryParams = (filters) => {
    let params = `page=${pageNumber}&per_page=${itemPerPage}`;

    // Use debounced search value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`;
    }

    const {
      duration,
      touches,
      priority,
      contacts,
      task_type,
      repeat_number,
      link_other_smartplan,
      sales_associate,
      created_at,
      created_start_date,
      created_end_date
    } = filters;

    if (duration !== "") params += `&duration=${duration}`;
    if (touches !== "") params += `&touches=${touches}`;
    if (task_type !== "") params += `&task_type=${task_type}`;
    if (repeat_number !== "no") params += `&repeat_number=${repeat_number}`;
    if (link_other_smartplan?.length > 0)
      params += `&link_other_smartplan=${link_other_smartplan.join(",")}`;
    if (priority?.length > 0)
      params += `&priority=${encodeURIComponent(priority.join(","))}`;
    if (contacts?.length > 0)
      params += `&contact_id=${encodeURIComponent(contacts.map((el) => el?.id).join(","))}`;
    if (sales_associate) params += `&sales_associate=${sales_associate}`;
      if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`
    if (sortDirection && sortBy !== "id")
      params += `&sort_direction=${sortDirection}`;
    if (sortBy !== "id") params += `&sort_by=${sortBy}`;

    return params;
  };

  const buildQueryParamsTab2 = (filters) => {
    let params = `page=${pageNumberTab2}&per_page=${itemPerPageTab2}`;

    // Use debounced search value
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`;
    }

    const {
      duration,
      touches,
      priority,
      contacts,
      task_type,
      repeat_number,
      link_other_smartplan,
      sales_associate,
      created_at,
      created_start_date,
      created_end_date
    } = filters;

    if (duration !== "") params += `&duration=${duration}`;
    if (touches !== "") params += `&touches=${touches}`;
    if (task_type !== "") params += `&task_type=${task_type}`;
    if (repeat_number !== "no") params += `&repeat_number=${repeat_number}`;
    if (link_other_smartplan?.length > 0)
      params += `&link_other_smartplan=${link_other_smartplan.join(",")}`;
    if (priority?.length > 0)
      params += `&priority=${encodeURIComponent(priority.join(","))}`;
    if (contacts?.length > 0)
      params += `&contact_id=${encodeURIComponent(contacts.map((el) => el?.id).join(","))}`;
    if (sales_associate) params += `&sales_associate=${sales_associate}`;
    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;

    return params;
  };

  const fetchSmartPlans = (filters = filterData) => {
    const queryParams = buildQueryParams(filters);
    setLoading(true);

    axios
      .get(`${BASE_URL}/smartlisting?active_tab=${activeTab}&${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.smartplans;
        setSmartPlanData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardDataFilter = (filters = filterData) => {
    const queryParams = buildQueryParams(filters);
    setLoading(true);
    axios
      .get(`${BASE_URL}/smartlisting-touchplan?active_tab=${activeTab}&${queryParams}`, config)
      .then((res) => {
        if (res?.data?.success) {
          setTouchPlanUserData(res.data.smartplans || []);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSmartPlans(filterData);
    fetchDashboardDataFilter(filterData);
  }, [sortBy, sortDirection, activeTab, debouncedSearch, pageNumber, itemPerPage]);

  const onSuccess = (filters) => {
    const appliedFilters = filters || filterData;
    fetchSmartPlans(appliedFilters);
    fetchDashboardDataFilter(appliedFilters);
    if (activeTab === 2) {
      fetchTouchPlanTasksData(appliedFilters);
    }
  };

  // Reset page when search or tab changes
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, activeTab]);

  const handleDelete = () => {
    setDisableDelete(true);

    axios
      .delete(`${BASE_URL}/delete-smartplan/${selectedId}`, config)
      .then((res) => {
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
        setSelectedId("");
        setShowDeleteModal(false);
        onSuccess();
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const fetchDashboardData = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/dashboard-touch-plan?active_tab=${activeTab}`, config)
      .then((res) => {
        setSmartplanDashboard(res?.data?.touch_plan);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchTouchPlanUserData = () => {
    setLoading(true);
    const params = `page=${pageNumberTab2}&per_page=${itemPerPageTab2}`;

    axios
      .get(`${BASE_URL}/smartlisting-touchplan?active_tab=${activeTab}&${params}`, config)
      .then((res) => {
        if (res?.data?.success) {
          setTouchPlanUserData(res.data.smartplans || []);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

   useEffect(() => {
    fetchTouchPlanUserData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchTouchPlanUserData();
    }
  }, [activeTab, pageNumberTab2, itemPerPageTab2]);

  const fetchTouchPlanTasksData = (filters = filterData) => {
    setLoading(true);
    const queryParams = buildQueryParamsTab2(filters);

    axios
      .get(`${BASE_URL}/smartlisting-touchplan?active_tab=${activeTab}&${queryParams}`, config)
      .then((res) => {
        if (res?.data?.success) {
          setTouchPlanTasksData(res.data.data || []);
          setTouchPlanPaginationData({
            totalItems: res.data.pagination?.total ?? 0,
            from: res.data.pagination?.from ?? 0,
            to: res.data.pagination?.to ?? 0,
            totalPages: res.data.pagination?.last_page ?? 0,
          });
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleNextTab2 = () => {
    if (pageNumberTab2 < touchPlanPaginationData.totalPages) {
      setPageNumberTab2(pageNumberTab2 + 1);
    }
  };

  const handlePrevTab2 = () => {
    if (pageNumberTab2 > 1) {
      setPageNumberTab2(pageNumberTab2 - 1);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchTouchPlanTasksData();
    }
  }, [activeTab, pageNumberTab2, itemPerPageTab2]);

  const BarGraphData = [
    { name: "Jan", count: smartplanDashboard?.month_graph_data?.Jan, key: "jan" },
    { name: "Feb", count: smartplanDashboard?.month_graph_data?.Feb, key: "feb" },
    { name: "Mar", count: smartplanDashboard?.month_graph_data?.Mar, key: "mar" },
    { name: "Apr", count: smartplanDashboard?.month_graph_data?.Apr, key: "apr" },
    { name: "May", count: smartplanDashboard?.month_graph_data?.May, key: "may" },
    { name: "Jun", count: smartplanDashboard?.month_graph_data?.Jun, key: "jun" },
    { name: "Jul", count: smartplanDashboard?.month_graph_data?.Jul, key: "jul" },
    { name: "Aug", count: smartplanDashboard?.month_graph_data?.Aug, key: "aug" },
    { name: "Sep", count: smartplanDashboard?.month_graph_data?.Sep, key: "sep" },
    { name: "Oct", count: smartplanDashboard?.month_graph_data?.Oct, key: "oct" },
    { name: "Nov", count: smartplanDashboard?.month_graph_data?.Nov, key: "nov" },
    { name: "Dec", count: smartplanDashboard?.month_graph_data?.Dec, key: "dec" },
  ];

  const weeklyData = [
    { name: "MON", count: smartplanDashboard?.week_graph_data?.Mon },
    { name: "TUE", count: smartplanDashboard?.week_graph_data?.Tue },
    { name: "WED", count: smartplanDashboard?.week_graph_data?.Wed },
    { name: "THU", count: smartplanDashboard?.week_graph_data?.Thu },
    { name: "FRI", count: smartplanDashboard?.week_graph_data?.Fri },
    { name: "SAT", count: smartplanDashboard?.week_graph_data?.Sat },
    { name: "SUN", count: smartplanDashboard?.week_graph_data?.Sun },
  ];

  const salesAssociates = smartplanDashboard?.contact_data || [];

  // Group data by smartplan_id
  const groupedTouchPlanData = touchPlanUserData.reduce((acc, item) => {
    const key = item.smartplan_id;
    if (!acc[key]) {
      acc[key] = {
        smartplan_id: key,
        smartplan_name: item.smartplan_name || `TouchPlan #${key}`,
        users: [],
      };
    }
    acc[key].users.push({
      user_id: item.user_id,
      user_full_name: item.user_full_name || `User #${item.user_id}`,
      ongoing_count: item.unique_ongoing_contacts_count || 0,
      ongoing_names: item.ongoing_contact_names || "-",
      completed_count: item.unique_completed_contacts_count || 0,
      completed_names: item.completed_contact_names || "-",
    });
    return acc;
  }, {});

  const groupedArray = Object.values(groupedTouchPlanData);

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">TouchPlans</p>
      </div>

      <div className="md:flex justify-between items-center mb-4 mt-2 px-1">
        <div className="flex gap-4 items-center flex-wrap">
          <SmartPlanFilter
            filterData={filterData}
            onSetFilterData={setFilterData}
            onCallApiAgain={onSuccess}
          />

          <div className="search-box contacts relative w-[250px]">
            <input
              type="text"
              className="body-S"
              placeholder="Search name, contact count, duration, touches..."
              value={search}
              onChange={handleSearchChange}
              style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
            />
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <button
            className="add-contact-button green-bg-H light-L body-S"
            onClick={() => setShowModal(true)}
          >
            <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Create
          </button>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex body-L dark-M mb-4">
        <div className="flex gap-4">
          {["8x8 Team Dashboard", "TouchPlans List", "TouchPlan History"].map(
            (tab, index) => (
              <p
                key={index}
                role="button"
                className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
                onClick={() => {
                  setActiveTab(index);
                  setPageNumber(1);
                  setPageNumberTab2(1);
                }}
              >
                {tab}
              </p>
            )
          )}
        </div>
      </div>

      {activeTab === 0 && (
        <div className="relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px] rounded-lg">
              <Loader />
            </div>
          )}
          <div className="flex flex-col lg:flex-row gap-4 w-full">
            <div
              className={
                salesAssociates?.length > 0
                  ? "flex-1 space-y-6 h-[50%]"
                  : "w-full flex flex-col lg:flex-row gap-6"
              }
            >
              <div className={`bg-white p-6 rounded-lg ${salesAssociates?.length === 0 ? "flex-1" : ""}`}>
                <SmartplanBarcharts
                  className="w-[100%]"
                  Title="Total 8x8s by Month"
                  BarGraphData={BarGraphData}
                />
              </div>
              <div className={`bg-white p-6 rounded-lg ${salesAssociates?.length === 0 ? "flex-1" : ""}`}>
                <SmartplanBarcharts
                  className="w-[100%]"
                  Title="Total 8x8s This Week"
                  BarGraphData={weeklyData}
                />
              </div>
            </div>

            {salesAssociates?.length > 0 && (
              <div className="flex-1">
                <div className="green-bg-L text-center text-green-900 font-semibold py-3 rounded-t-lg">
                  8x8s This Week by Sales Associate
                </div>
                <div className="rounded-b-lg shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {salesAssociates.map((associate, index) => (
                      <div
                        key={index}
                        className="bg-white green-bg-50 p-6 rounded-lg border border-green-100"
                      >
                        <div className="flex items-center gap-6">
                          <div className="w-10 h-10 green-bg flex items-center justify-center rounded-full">
                            <img src={account} alt="" className="sidebar-icons" />
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-800">
                              {associate.name}
                            </p>
                            <p className="text-lg font-bold text-green-900">
                              {associate.count}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
              <Loader />
            </div>
          )}
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th className="green-H ">
                      <div className="table-header">
                        Name
                        <img
                          role="button"
                          src={sortBy !== "name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("name");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                          alt="sort icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Contacts
                        <img
                          role="button"
                          src={sortBy !== "contact" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="sort icon"
                          onClick={() => {
                            setSortBy("contact");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Created
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="sort icon"
                          onClick={() => {
                            setSortBy("created_at");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Duration
                        <img
                          role="button"
                          src={sortBy !== "duration" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="sort icon"
                          onClick={() => {
                            setSortBy("duration");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Total Touches
                        <img
                          role="button"
                          src={sortBy !== "touches_count" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="sort icon"
                          onClick={() => {
                            setSortBy("touches_count");
                            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                          }}
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">More</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, idx) => (
                      <tr key={idx} className="dark-H body-N">
                        <td><Skeleton height="20px" width="140px" /></td>
                        <td><Skeleton height="20px" width="40px" /></td>
                        <td><Skeleton height="20px" width="80px" /></td>
                        <td><Skeleton height="20px" width="60px" /></td>
                        <td><Skeleton height="20px" width="40px" /></td>
                        <td><Skeleton height="20px" width="40px" /></td>
                      </tr>
                    ))
                  ) : smartPlanData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="body-N text-center mt-6 p-10 border-0">
                        No TouchPlan Found
                      </td>
                    </tr>
                  ) : (
                    smartPlanData.map((el, idx) => (
                      <tr key={idx} className="dark-H body-N">
                      <td className="head-5 flex items-center gap-2">
                        <Link
                          to={ROUTES.TOUCH_PLAN_DETAILS.replace(":id", el?.id)}
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(ROUTES.TOUCH_PLAN_DETAILS.replace(":id", el?.id));
                          }}
                        >
                          {el?.name}
                        </Link>
                      </td>

                      <td className="dark-M head-5">
                        <div className="flex items-center pl-8">
                          <p>{el?.contact ? el?.contact?.length : 0}</p>
                          <img
                            role="button"
                            src={Eye}
                            className="ml-2"
                            alt="eye icon"
                            onClick={() => {
                              setShowLinkModal(true);
                              setSelectedId(el?.id);
                              setSelectedContact(el?.contact_details);
                              setContactCounts({
                                contactWeekCount: el?.contact_count_week_month?.contactWeekCount || 0,
                                contactMonthCount: el?.contact_count_week_month?.contactMonthCount || 0,
                                contactLastWeekCount: el?.contact_count_week_month?.contactLastWeekCount || 0,
                                contactLastMonthCount: el?.contact_count_week_month?.contactLastMonthCount || 0,
                              });
                            }}
                          />
                        </div>
                      </td>

                      <td>{moment(el?.created_at).tz("America/New_York").format("MM/DD/YY")}</td>
                      <td className="flex items-center gap-2">
                        <p className="tags green-H body-S capitalize">
                          {el.duration} {el.duration > 1 ? "Days" : "Day"}
                        </p>
                      </td>
                      <td className="pl-20">{el?.touches_count}</td>
                      <td className="flex gap-3 items-center">
                        <ActionsMenu
                          handleEdit={() => {
                            navigate(`/touch-plan/${el?.id}`);
                            setSelectedId(el?.id);
                          }}
                          handleDelete={() => {
                            setSelectedId(el?.id);
                            setShowDeleteModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
          </div>
          {smartPlanData.length !== 0 && (
            <ContactPagination
              paginationData={paginationData}
              activeTab={activeTab}
              handlePrev={handlePrev}
              handleNext={handleNext}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setPageNumber(1);
              }}
            />
          )}
          <div className="px-6 pb-2">
            <DataAsOfNotice />
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
              <Loader />
            </div>
          )}
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th className="green-H">
                      <div className="table-header">Date Added</div>
                    </th>
                    <th>
                      <div className="table-header">TouchPlan Name</div>
                    </th>
                    <th>
                      <div className="table-header">Contact Name</div>
                    </th>
                    <th>
                      <div className="table-header">Sales Associate</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, idx) => (
                      <tr key={idx} className="dark-H body-N">
                        <td><Skeleton height="20px" width="80px" /></td>
                        <td><Skeleton height="20px" width="140px" /></td>
                        <td><Skeleton height="20px" width="140px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                      </tr>
                    ))
                  ) : touchPlanTasksData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="body-N text-center mt-6 p-10 border-0">
                        No TouchPlan tasks found
                      </td>
                    </tr>
                  ) : (
                    touchPlanTasksData.map((item, idx) => (
                      <tr key={idx} className="dark-H body-N">
                      <td className="head-5">{item.date_added}</td>
                      <td className="head-5">
                        <Link
                          to={ROUTES.TOUCH_PLAN_DETAILS.replace(":id", item.smartplan_id)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.smartplan_id) {
                              navigate(ROUTES.TOUCH_PLAN_DETAILS.replace(":id", item.smartplan_id));
                            }
                          }}
                        >
                          {item.touchplan_name}
                        </Link>
                      </td>
                      <td className="head-5">
                        <Link
                          to={ROUTES.CONTACT_DETAILS.replace(":id", item.contact_id)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.contact_id) {
                              navigate(ROUTES.CONTACT_DETAILS.replace(":id", item.contact_id));
                            }
                          }}
                        >
                          {item.contact_name}
                        </Link>
                      </td>
                      <td className="head-5">
                        <Link
                          to={ROUTES.USER_DETAILS.replace(":id", item.sales_associate_id)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.sales_associate_id) {
                              navigate(ROUTES.USER_DETAILS.replace(":id", item.sales_associate_id));
                            }
                          }}
                        >
                          {item.sales_associate}
                        </Link>
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
          </div>
          {touchPlanTasksData.length !== 0 && (
            <ContactPagination
              paginationData={touchPlanPaginationData}
              activeTab={activeTab}
              handlePrev={handlePrevTab2}
              handleNext={handleNextTab2}
              onSuccess={fetchTouchPlanTasksData}
              itemPerPage={itemPerPageTab2}
              handleItemPerPageSelect={(value) => {
                setItemPerPageTab2(value);
                setPageNumberTab2(1);
              }}
            />
          )}
          <div className="px-6 pb-2">
            <DataAsOfNotice  />
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateSmartPlanModal showModal={showModal} onClose={() => setShowModal(false)} />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      <LinkContactInfoModal
        linkContacts={selectedContacts}
        id={selectedId}
        showModal={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSuccess={onSuccess}
        fetchSmartPlans={fetchSmartPlans}
        contactCounts={contactCounts}
      />
    </BaseLayout>
  );
};

export default Smartplan;